import { LoadChildrenCallback } from '@angular/router';
import { EUserPermission } from '@project/shared';
import { TranslateService } from '@project/translate';
import { ESidebarIconType } from './icons';
import { EFeatureFlags } from './feature-flags';
import { RoutesBuilderService } from './routes-builder.service';
import { HCConfigFeatures, HCConfigRules } from '../../hc-config/hc-config';

export interface ISiteModule {
  rootRouteBase: string;
  rootFullRoute: string;
  sidebarLabelCreator: () => string;
  browserTabTitleCreator: () => string;
  lazyModule: LoadChildrenCallback;
  sidebarIcon: ESidebarIconType;
  permissions?: EUserPermission[];
  permissionCheckFailRedirectUrl?: string;
  featureFlagsToCheck?: EFeatureFlags[];
  hasHcFeature?: HCConfigFeatures[];
  hasHcRule?: HCConfigRules[];
}

export const SITE_USER_MODULES: ISiteModule[] = [
  {
    rootRouteBase: RoutesBuilderService.HEALTH_CENTERS_LIST.base,
    rootFullRoute: RoutesBuilderService.HEALTH_CENTERS_LIST.root(),
    sidebarIcon: ESidebarIconType.MedicalSign,
    lazyModule: () => import('../lazy-modules/health-centers/health-centers.module').then((m) => m.HealthCentersModule),
    browserTabTitleCreator: () => TranslateService.localize('sidebar.menu-labels.health-centers'),
    sidebarLabelCreator: () => TranslateService.localize('sidebar.menu-labels.health-centers'),
    permissions: [EUserPermission.manageHealthCenters],
  },
  {
    rootRouteBase: RoutesBuilderService.DOCTORS_LIST.base,
    rootFullRoute: RoutesBuilderService.DOCTORS_LIST.root(),
    sidebarIcon: ESidebarIconType.People,
    lazyModule: () => import('../lazy-modules/doctors/doctors.module').then((m) => m.DoctorsModule),
    browserTabTitleCreator: () => TranslateService.localize('sidebar.menu-labels.doctors'),
    sidebarLabelCreator: () => TranslateService.localize('sidebar.menu-labels.doctors'),
    permissions: [EUserPermission.manageDoctors],
  },
  {
    rootRouteBase: RoutesBuilderService.PATIENTS_LIST.base,
    rootFullRoute: RoutesBuilderService.PATIENTS_LIST.root(),
    sidebarIcon: ESidebarIconType.People,
    lazyModule: () => import('../lazy-modules/patients/patients.module').then((m) => m.PatientsModule),
    browserTabTitleCreator: () => TranslateService.localize('sidebar.menu-labels.patients'),
    sidebarLabelCreator: () => TranslateService.localize('sidebar.menu-labels.patients'),
    permissions: [EUserPermission.managePatients],
  },
  {
    rootRouteBase: RoutesBuilderService.ADMINISTRATORS_LIST.base,
    rootFullRoute: RoutesBuilderService.ADMINISTRATORS_LIST.root(),
    sidebarIcon: ESidebarIconType.People,
    lazyModule: () =>
      import('../lazy-modules/administrators/administrators.module').then((m) => m.AdministratorsModule),
    browserTabTitleCreator: () => TranslateService.localize('sidebar.menu-labels.administrators'),
    sidebarLabelCreator: () => TranslateService.localize('sidebar.menu-labels.administrators'),
    permissions: [EUserPermission.manageGeneralAdmins],
  },
  {
    rootRouteBase: RoutesBuilderService.HEALTH_CENTERS_ADMINISTRATORS_LIST.base,
    rootFullRoute: RoutesBuilderService.HEALTH_CENTERS_ADMINISTRATORS_LIST.root(),
    sidebarIcon: ESidebarIconType.People,
    lazyModule: () =>
      import('../lazy-modules/health-centers-admins/health-centers-admins.module').then(
        (m) => m.HealthCentersAdminsModule,
      ),
    browserTabTitleCreator: () => TranslateService.localize('sidebar.menu-labels.health-center-administrators'),
    sidebarLabelCreator: () => TranslateService.localize('sidebar.menu-labels.health-center-administrators'),
    permissions: [EUserPermission.manageHealthCenterAdmins],
  },
  {
    rootRouteBase: RoutesBuilderService.GROUPS_MANAGEMENT.base,
    rootFullRoute: RoutesBuilderService.GROUPS_MANAGEMENT.root(),
    sidebarIcon: ESidebarIconType.People,
    lazyModule: () =>
      import('../lazy-modules/groups-management/groups-management.module').then((m) => m.GroupsManagementModule),
    browserTabTitleCreator: () => TranslateService.localize('sidebar.menu-labels.group-management'),
    sidebarLabelCreator: () => TranslateService.localize('sidebar.menu-labels.group-management'),
    permissions: [EUserPermission.manageHealthCenterAdmins],
  },
  {
    rootRouteBase: RoutesBuilderService.OBSERVATIONS.base,
    rootFullRoute: RoutesBuilderService.OBSERVATIONS.root(),
    sidebarIcon: ESidebarIconType.Eye,
    lazyModule: () => import('../lazy-modules/observations/observations.module').then((m) => m.ObservationsModule),
    browserTabTitleCreator: () => TranslateService.localize('sidebar.menu-labels.observations'),
    sidebarLabelCreator: () => TranslateService.localize('sidebar.menu-labels.observations'),
    permissions: [EUserPermission.viewObservations],
    featureFlagsToCheck: [EFeatureFlags.observations],
  },
  {
    rootRouteBase: RoutesBuilderService.RECEPTIONISTS_LIST.base,
    rootFullRoute: RoutesBuilderService.RECEPTIONISTS_LIST.root(),
    sidebarIcon: ESidebarIconType.People,
    lazyModule: () => import('../lazy-modules/receptionists/receptionists.module').then((m) => m.ReceptionistsModule),
    browserTabTitleCreator: () => TranslateService.localize('sidebar.menu-labels.receptionists'),
    sidebarLabelCreator: () => TranslateService.localize('sidebar.menu-labels.receptionists'),
    permissions: [EUserPermission.manageReceptionists],
  },
  {
    rootRouteBase: RoutesBuilderService.APPOINTMENT_MANAGEMENT.base,
    rootFullRoute: RoutesBuilderService.APPOINTMENT_MANAGEMENT.root(),
    sidebarIcon: ESidebarIconType.Calendar,
    lazyModule: () =>
      import('../lazy-modules/appointment-management/appointment-management.module').then(
        (m) => m.AppointmentManagementModule,
      ),
    browserTabTitleCreator: () => TranslateService.localize('sidebar-menu.labels.appointment-management'),
    sidebarLabelCreator: () => TranslateService.localize('sidebar-menu.labels.appointment-management'),
    permissions: [EUserPermission.appointmentManagement],
    hasHcRule: [HCConfigRules.APPOINTMENT_MANAGEMENT],
  },
  {
    rootRouteBase: RoutesBuilderService.PRIORITY_APPOINTMENT.base,
    rootFullRoute: RoutesBuilderService.PRIORITY_APPOINTMENT.root(),
    sidebarIcon: ESidebarIconType.Calendar,
    lazyModule: () =>
      import('../lazy-modules/priority-appointment/priority-appointment.module').then(
        (m) => m.PriorityAppointmentModule,
      ),
    browserTabTitleCreator: () => TranslateService.localize('sidebar-menu.labels.priority-appointment'),
    sidebarLabelCreator: () => TranslateService.localize('sidebar-menu.labels.priority-appointment'),
    permissions: [EUserPermission.priorityAppointment],
  },
  {
    rootRouteBase: RoutesBuilderService.LIVE_QUEUE_COCKPIT.base,
    rootFullRoute: RoutesBuilderService.LIVE_QUEUE_COCKPIT.root(),
    sidebarIcon: ESidebarIconType.Desktop,
    lazyModule: () => import('../lazy-modules/cockpit-v1/cockpit-v1.module').then((m) => m.CockpitV1Module),
    browserTabTitleCreator: () => TranslateService.localize('sidebar-menu.labels.cockpit'),
    sidebarLabelCreator: () => TranslateService.localize('sidebar-menu.labels.cockpit'),
    permissions: [EUserPermission.manageLiveQueueCockpit],
  },
  {
    rootRouteBase: RoutesBuilderService.WORKING_HOURS.base,
    rootFullRoute: RoutesBuilderService.WORKING_HOURS.root(),
    sidebarIcon: ESidebarIconType.Calendar,
    lazyModule: () => import('../lazy-modules/working-hours/working-hours.module').then((m) => m.WorkingHoursModule),
    browserTabTitleCreator: () => TranslateService.localize('sidebar.menu-labels.working-hours'),
    sidebarLabelCreator: () => TranslateService.localize('sidebar.menu-labels.working-hours'),
    permissions: [EUserPermission.manageHealthCenterWorkingHours],
  },
  {
    rootRouteBase: RoutesBuilderService.SESSION_BY_RECEPTIONIST.base,
    rootFullRoute: RoutesBuilderService.SESSION_BY_RECEPTIONIST.root(),
    sidebarIcon: ESidebarIconType.Document,
    lazyModule: () =>
      import('../lazy-modules/sessions/sessions-receptionist/receptionist-sessions.module').then(
        (m) => m.ReceptionistSessionsModule,
      ),
    browserTabTitleCreator: () => TranslateService.localize('sidebar.menu-labels.sessions'),
    sidebarLabelCreator: () => TranslateService.localize('sidebar.menu-labels.sessions'),
    permissions: [EUserPermission.haveReceptionistsSession],
  },
  {
    rootRouteBase: RoutesBuilderService.MY_OBSERVATIONS_AS_PATIENT.base,
    rootFullRoute: RoutesBuilderService.MY_OBSERVATIONS_AS_PATIENT.root(),
    sidebarIcon: ESidebarIconType.PersonCard,
    lazyModule: () =>
      import('../lazy-modules/patient-observations/patient-observations.module').then(
        (m) => m.PatientObservationsModule,
      ),
    browserTabTitleCreator: () => TranslateService.localize('sidebar.menu-labels.my-observations'),
    sidebarLabelCreator: () => TranslateService.localize('sidebar.menu-labels.my-observations'),
    permissions: [EUserPermission.viewOwnObservationsAsPatient],
  },
  {
    rootRouteBase: RoutesBuilderService.ENCOUNTER_HISTORY_AS_PATIENT.base,
    rootFullRoute: RoutesBuilderService.ENCOUNTER_HISTORY_AS_PATIENT.root(),
    sidebarIcon: ESidebarIconType.Document,
    lazyModule: () =>
      import('../lazy-modules/patient-encounter-history/patient-encounter-history.module').then(
        (m) => m.PatientEncounterHistoryModule,
      ),
    browserTabTitleCreator: () => TranslateService.localize('sidebar.menu-labels.encounter-history'),
    sidebarLabelCreator: () => TranslateService.localize('sidebar.menu-labels.encounter-history'),
    permissions: [EUserPermission.havePatientSession],
  },
  {
    rootRouteBase: RoutesBuilderService.EXAMS_AS_PATIENT.base,
    rootFullRoute: RoutesBuilderService.EXAMS_AS_PATIENT.root(),
    sidebarIcon: ESidebarIconType.PersonCard,
    lazyModule: () => import('../lazy-modules/patient-exams/patient-exams.module').then((m) => m.PatientExamsModule),
    browserTabTitleCreator: () => TranslateService.localize('sidebar.menu-labels.patient-exams'),
    sidebarLabelCreator: () => TranslateService.localize('sidebar.menu-labels.patient-exams'),
    permissions: [EUserPermission.havePatientSession],
  },
  {
    rootRouteBase: RoutesBuilderService.DOCUMENTS_AS_PATIENT.base,
    rootFullRoute: RoutesBuilderService.DOCUMENTS_AS_PATIENT.root(),
    sidebarIcon: ESidebarIconType.PersonCard,
    lazyModule: () =>
      import('../lazy-modules/patient-documents/patient-documents.module').then((m) => m.PatientDocumentsModule),
    browserTabTitleCreator: () => TranslateService.localize('sidebar.menu-labels.patient-documents'),
    sidebarLabelCreator: () => TranslateService.localize('sidebar.menu-labels.patient-documents'),
    permissions: [EUserPermission.havePatientSession],
  },
  {
    rootRouteBase: RoutesBuilderService.SESSION_LOGS.base,
    rootFullRoute: RoutesBuilderService.SESSION_LOGS.root(),
    sidebarIcon: ESidebarIconType.PersonCard,
    lazyModule: () => import('../lazy-modules/sessions-logs/sessions-logs.module').then((m) => m.SessionsLogsModule),
    browserTabTitleCreator: () => TranslateService.localize('sidebar.menu-labels.sessions-logs'),
    sidebarLabelCreator: () => TranslateService.localize('sidebar.menu-labels.sessions-logs'),
    permissions: [EUserPermission.manageSessionsLogs],
  },
  {
    rootRouteBase: RoutesBuilderService.LOGINS_LOGS.base,
    rootFullRoute: RoutesBuilderService.LOGINS_LOGS.root(),
    sidebarIcon: ESidebarIconType.PersonCard,
    lazyModule: () => import('../lazy-modules/login-logs/login-logs.module').then((m) => m.LoginLogsModule),
    browserTabTitleCreator: () => TranslateService.localize('sidebar.menu-labels.login-logs'),
    sidebarLabelCreator: () => TranslateService.localize('sidebar.menu-labels.login-logs'),
    permissions: [EUserPermission.manageLoginLogs],
  },
  {
    rootRouteBase: RoutesBuilderService.BENEFICIARES_DEACTIVATION.base,
    rootFullRoute: RoutesBuilderService.BENEFICIARES_DEACTIVATION.root(),
    sidebarIcon: ESidebarIconType.PersonCard,
    lazyModule: () =>
      import('../lazy-modules/beneficiaries-exclusion/beneficiaries-deactivate.module').then(
        (m) => m.BeneficiariesDeactivateModule,
      ),
    browserTabTitleCreator: () => TranslateService.localize('sidebar.menu-labels.beneficiary-deactivation'),
    sidebarLabelCreator: () => TranslateService.localize('sidebar.menu-labels.beneficiary-deactivation'),
    permissions: [EUserPermission.manageDeactivationBeneficiaries],
    hasHcFeature: [HCConfigFeatures.BULK_DEACTIVATION_BENEFICIARIES],
  },
  {
    rootRouteBase: RoutesBuilderService.SENDGRID.base,
    rootFullRoute: RoutesBuilderService.SENDGRID.root(),
    sidebarIcon: ESidebarIconType.Dashboard,
    lazyModule: () => import('../lazy-modules/sendgrid/sendgrid.module').then((m) => m.SendGridModule),
    browserTabTitleCreator: () => TranslateService.localize('sidebar.menu-labels.sendgrid'),
    sidebarLabelCreator: () => TranslateService.localize('sidebar.menu-labels.sendgrid'),
    permissions: [EUserPermission.manageSendgrid],
  },
  {
    rootRouteBase: RoutesBuilderService.ENCOUNTER_HISTORY_AS_DOCTOR.base,
    rootFullRoute: RoutesBuilderService.ENCOUNTER_HISTORY_AS_DOCTOR.root(),
    sidebarIcon: ESidebarIconType.Document,
    lazyModule: () =>
      import('../lazy-modules/doctor-encounter-history/doctor-encounter-history.module').then(
        (m) => m.DoctorEncounterHistoryModule,
      ),
    browserTabTitleCreator: () => TranslateService.localize('sidebar.menu-labels.encounter-history'),
    sidebarLabelCreator: () => TranslateService.localize('sidebar.menu-labels.encounter-history'),
    permissions: [EUserPermission.haveDoctorSession],
  },
  {
    rootRouteBase: RoutesBuilderService.DIGITAL_SIGNATURE.base,
    rootFullRoute: RoutesBuilderService.DIGITAL_SIGNATURE.root(),
    sidebarIcon: ESidebarIconType.Signature,
    lazyModule: () =>
      import('../lazy-modules/digital-signature/digital-signature.module').then((m) => m.DigitalSignatureModule),
    browserTabTitleCreator: () => TranslateService.localize('sidebar.menu-labels.digital-signature'),
    sidebarLabelCreator: () => TranslateService.localize('sidebar.menu-labels.digital-signature'),
    permissions: [EUserPermission.haveDoctorSession],
  },
  {
    rootRouteBase: RoutesBuilderService.ENCOUNTER_HISTORY_AS_RECEPCIONIST.base,
    rootFullRoute: RoutesBuilderService.ENCOUNTER_HISTORY_AS_RECEPCIONIST.root(),
    sidebarIcon: ESidebarIconType.Document,
    lazyModule: () =>
      import('../lazy-modules/recepcionist-encounter-history/recepcionist-encounter-history.module').then(
        (m) => m.RecepcionistEncounterHistoryModule,
      ),
    browserTabTitleCreator: () => TranslateService.localize('sidebar.menu-labels.encounter-history'),
    sidebarLabelCreator: () => TranslateService.localize('sidebar.menu-labels.encounter-history'),
    permissions: [EUserPermission.haveReceptionistsSession],
  },
];

export const SITE_USER_SETTINGS_MODULES: ISiteModule[] = [
  {
    rootRouteBase: RoutesBuilderService.SETTINGS.base,
    rootFullRoute: RoutesBuilderService.SETTINGS.root(),
    sidebarIcon: ESidebarIconType.Settings,
    lazyModule: () => import('../lazy-modules/settings/settings.module').then((m) => m.SettingsModule),
    browserTabTitleCreator: () => TranslateService.localize('sidebar.menu-labels.main-settings'),
    sidebarLabelCreator: () => TranslateService.localize('sidebar.menu-labels.main-settings'),
  },
  {
    rootRouteBase: RoutesBuilderService.HEALTH_CENTER_SETTINGS.base,
    rootFullRoute: RoutesBuilderService.HEALTH_CENTER_SETTINGS.root(),
    sidebarIcon: ESidebarIconType.MedicalSign,
    lazyModule: () => import('../lazy-modules/hc-settings/hc-settings.module').then((m) => m.HcSettingsModule),
    browserTabTitleCreator: () => TranslateService.localize('sidebar.menu-labels.hc-settings'),
    sidebarLabelCreator: () => TranslateService.localize('sidebar.menu-labels.hc-settings'),
    permissions: [EUserPermission.manageSelfHealthCenterSettings],
  },
];
